import BaseService from 'services/base-service';
import type { AuthEventEmitter } from '@thoughtspot/visual-embed-sdk';
import { thoughtspotApiUrl } from 'constants/config';
import type { InsightsApi } from '@env0/insights-service/api';
import type { Env0Theme } from 'constants/themes.constants';
import { getThoughtspotCustomizations } from 'constants/thoughtspot-theme-mapping';

export type ThoughtspotFeature = InsightsApi.InsightsContextName | 'insights';

const loadThoughtspotSdk = () => import('@thoughtspot/visual-embed-sdk');

export class ThoughtspotService extends BaseService {
  private logoutPromise: Promise<boolean> | null = null;
  private initializingPromise: Promise<void> | null = null;
  private initialized: string | null = null;
  private authStatus: AuthEventEmitter | null = null;
  userId: string | null = null;

  init = async (
    theme: Env0Theme,
    organizationId: string,
    feature: ThoughtspotFeature,
    onAuthFailed: () => void
  ): Promise<void> => {
    const wantToInitialize = `${theme.mode}-${organizationId}-${feature}`;

    /**
     * Callers arrive concurrently — the user switched pages quickly, or (since APO-481) flipped the
     * theme, which re-keys the embed. Each one is queued behind the last and tests `initialized` when
     * its turn comes, not when it was called, so the SDK ends in the state the *last* caller asked for.
     *
     * Chained rather than wait-and-re-enter: a waiter that re-entered read `initialized` before the
     * waiter ahead of it had started, so a light → dark → light flip left the SDK dark while the app was
     * light, and termination rode on the owner's `finally` winning a microtask race against the waiter.
     *
     * `catch` before `then` keeps a failed init from wedging the queue; the rejection still reaches the
     * caller that owned it, because that is the promise this call returns.
     */
    this.initializingPromise = (this.initializingPromise ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => {
        if (this.initialized === wantToInitialize) return;

        return this.runInit(theme, organizationId, feature, onAuthFailed, wantToInitialize);
      });

    return this.initializingPromise;
  };

  private runInit = async (
    theme: Env0Theme,
    organizationId: string,
    feature: ThoughtspotFeature,
    onAuthFailed: () => void,
    wantToInitialize: string
  ) => {
    const customizations = getThoughtspotCustomizations(theme);

    if (this.initialized) {
      await this.logout();
    }

    const { AuthStatus, AuthType, init } = await loadThoughtspotSdk();

    this.authStatus = init({
      callPrefetch: true,
      authType: AuthType.TrustedAuthTokenCookieless,
      thoughtSpotHost: thoughtspotApiUrl,
      autoLogin: true,
      suppressErrorAlerts: true,
      getAuthToken: async () => {
        switch (feature) {
          case 'insights':
            return this.service.apiClient.insights.getToken(organizationId!).then(response => {
              this.userId = response.userId;
              return response.token;
            });
          case 'moduleUsage':
            return this.service.apiClient.insights
              .getSharedTokenByContext({ organizationId: organizationId!, contextName: feature })
              .then(response => response.token);
          default:
            throw new Error('Feature not supported');
        }
      },
      customizations
    });
    this.authStatus?.on(AuthStatus.FAILURE, onAuthFailed);

    // `initializingPromise` is cleared in `init`'s finally, never here: a concurrent caller keys off it
    // to decide whether to wait, and clearing it early lets a second init start on top of this one.
    this.initialized = wantToInitialize;
  };

  logout = async () => {
    const { AuthStatus, logout } = await loadThoughtspotSdk();

    if (!this.logoutPromise) {
      this.logoutPromise = logout();
    }

    await this.logoutPromise;

    this.logoutPromise = null;
    this.initialized = null;
    this.authStatus?.removeAllListeners(AuthStatus.FAILURE);
    this.authStatus = null;
  };
}
